import get from 'lodash/get';
import type * as Types from './types';

export const Question = (item?: any): Types.Question => ({
  // Spread first so every extended type-specific field (blanks, reorderItems,
  // …) survives; normalize the base fields after.
  ...(item || {}),
  id: get(item, 'id') || get(item, '_id') || '',
  type: get(item, 'type') || 'MULTIPLE_CHOICE',
  prompt: get(item, 'prompt') || '',
  options: get(item, 'options') || [],
  matchPairs: get(item, 'matchPairs') || [],
  points: get(item, 'points') || 1,
  timeLimit: get(item, 'timeLimit'),
  explanation: get(item, 'explanation'),
  order: get(item, 'order') || 0,
});

export const Section = (item?: any): Types.Section => ({
  id: get(item, 'id') || get(item, '_id') || '',
  title: get(item, 'title') || 'Untitled Section',
  description: get(item, 'description'),
  order: get(item, 'order') || 0,
  questions: (get(item, 'questions') || []).map((q: any) => Question(q)),
});

export const TestSettings = (item?: any): Types.TestSettings => ({
  timeLimit: get(item, 'timeLimit'),
  passingScore: get(item, 'passingScore'),
  shuffleQuestions: get(item, 'shuffleQuestions') || false,
  shuffleOptions: get(item, 'shuffleOptions') || false,
  showResultsImmediately: get(item, 'showResultsImmediately') || false,
  allowReview: get(item, 'allowReview') || false,
  proctoring: {
    enabled: get(item, 'proctoring.enabled') || false,
    requireCamera: get(item, 'proctoring.requireCamera') || false,
    requireMicrophone: get(item, 'proctoring.requireMicrophone') || false,
    fullScreenMode: get(item, 'proctoring.fullScreenMode') || false,
    preventCopyPaste: get(item, 'proctoring.preventCopyPaste') || false,
  },
});

export const StartPageFormField = (item?: any): Types.StartPageFormField => ({
  id: get(item, 'id') || get(item, '_id') || '',
  label: get(item, 'label') || '',
  type: get(item, 'type') || 'text',
  mandatory: !!get(item, 'mandatory'),
  order: get(item, 'order') ?? 0,
});

// Meta toggles default to `true` when unset so existing tests (which never
// configured a start page) keep showing the timer, progress bar, and summary.
export const StartPageMeta = (item?: any): Types.StartPageMeta => ({
  showQuestionCount: get(item, 'showQuestionCount') !== false,
  showEstimatedDuration: get(item, 'showEstimatedDuration') !== false,
  showAttemptsRemaining: get(item, 'showAttemptsRemaining') !== false,
  showTimer: get(item, 'showTimer') !== false,
  showProgressBar: get(item, 'showProgressBar') !== false,
});

export const StartPageConfig = (item?: any): Types.StartPageConfig => ({
  instructions: get(item, 'instructions') || '',
  consent: get(item, 'consent') || '',
  consentRequired: !!get(item, 'consentRequired'),
  formFields: (get(item, 'formFields') || [])
    .map((f: any) => StartPageFormField(f))
    .sort((a: Types.StartPageFormField, b: Types.StartPageFormField) => (a.order ?? 0) - (b.order ?? 0)),
  meta: StartPageMeta(get(item, 'meta')),
});

export const TestForAttempt = (item?: any): Types.TestForAttempt => {
  const sections = (get(item, 'sections') || []).map((s: any) => Section(s));
  // On unless explicitly switched off, so tests saved before the setting keep the numbered view.
  const showSubQuestionsInNavigator = get(item, 'config.time.showSubQuestionsInNavigator') !== false;
  const totalQuestions = sections.reduce(
    (acc: number, s: Types.Section) =>
      acc +
      s.questions.reduce(
        (n: number, q: Types.Question) =>
          n + (showSubQuestionsInNavigator && q.type === 'PASSAGE' && q.subQuestions?.length ? q.subQuestions.length : 1),
        0,
      ),
    0,
  );
  const totalPoints = sections.reduce(
    (acc: number, s: Types.Section) => acc + s.questions.reduce((qAcc: number, q: Types.Question) => qAcc + q.points, 0),
    0
  );

  return {
    id: get(item, 'id') || get(item, '_id') || '',
    title: get(item, 'title') || 'Untitled Test',
    description: get(item, 'description'),
    settings: TestSettings(get(item, 'settings')),
    antiCheating: {
      antiCheatingMonitor: get(item, 'antiCheating.antiCheatingMonitor') || false,
      disableRightClick: get(item, 'antiCheating.disableRightClick') || false,
      disableCopyPaste: get(item, 'antiCheating.disableCopyPaste') || false,
      lockdownBrowser: get(item, 'antiCheating.lockdownBrowser') || false,
    },
    // Configured on the Time settings page (config.time). Undefined when never
    // set — the runtime treats only an explicit `false` as "locked".
    allowFlexibleNavigation: get(item, 'config.time.allowFlexibleNavigation'),
    showSubQuestionsInNavigator,
    perQuestionSeconds: get(item, 'perQuestionSeconds') ?? null,
    answerEliminator: get(item, 'answerEliminator') === true,
    startPage: StartPageConfig(get(item, 'config.startPage')),
    sections,
    totalQuestions,
    totalPoints,
    category: get(item, 'category') || undefined,
    tags: get(item, 'tags') || [],
    subjectConfigs: get(item, 'subjectConfigs') || get(item, 'subject_configs') || [],
    examConfig: get(item, 'examConfig') || undefined,
  };
};

export const AttemptAnswer = (item?: any): Types.AttemptAnswer => ({
  id: get(item, 'id') || get(item, '_id') || '',
  attemptId: get(item, 'attemptId') || '',
  questionId: get(item, 'questionId') || '',
  value: get(item, 'value') || '',
  isCorrect: get(item, 'isCorrect'),
  pointsAwarded: get(item, 'pointsAwarded'),
  aiReasoning: get(item, 'aiReasoning'),
  aiConfidence: get(item, 'aiConfidence'),
  gradedAt: get(item, 'gradedAt'),
});

export const AttemptEvent = (item?: any): Types.AttemptEvent => ({
  id: get(item, 'id') || get(item, '_id') || '',
  attemptId: get(item, 'attemptId') || '',
  type: get(item, 'type') || 'TAB_SWITCH',
  timestamp: get(item, 'timestamp') || new Date().toISOString(),
  payload: get(item, 'payload'),
});

export const Attempt = (item?: any): Types.IEntity.Attempt => ({
  id: get(item, 'id') || get(item, '_id') || '',
  testId: get(item, 'testId') || '',
  candidateId: get(item, 'candidateId') || '',
  status: get(item, 'status') || 'IN_PROGRESS',
  startedAt: get(item, 'startedAt') ? new Date(get(item, 'startedAt')) : new Date(),
  submittedAt: get(item, 'submittedAt') ? new Date(get(item, 'submittedAt')) : undefined,
  gradedAt: get(item, 'gradedAt') ? new Date(get(item, 'gradedAt')) : undefined,
  score: get(item, 'score'),
  maxScore: get(item, 'maxScore'),
  tabSwitches: get(item, 'tabSwitches') || 0,
  answers: (get(item, 'answers') || []).map((a: any) => AttemptAnswer(a)),
  events: (get(item, 'events') || []).map((e: any) => AttemptEvent(e)),
  test: TestForAttempt(get(item, 'test')),
  candidate: get(item, 'candidate')
    ? {
        id: get(item, 'candidate.id') || '',
        name: get(item, 'candidate.name') || '',
        email: get(item, 'candidate.email') || '',
      }
    : null,
});

export const AttemptSummary = (item?: any): Types.IEntity.AttemptSummary => ({
  id: get(item, 'id') || get(item, '_id') || '',
  status: get(item, 'status') || 'IN_PROGRESS',
  startedAt: get(item, 'startedAt') ? new Date(get(item, 'startedAt')) : new Date(),
  submittedAt: get(item, 'submittedAt') ? new Date(get(item, 'submittedAt')) : undefined,
  score: get(item, 'score'),
  maxScore: get(item, 'maxScore'),
  tabSwitches: get(item, 'tabSwitches') || 0,
  candidate: get(item, 'candidate')
    ? {
        id: get(item, 'candidate.id') || '',
        name: get(item, 'candidate.name') || '',
        email: get(item, 'candidate.email') || '',
      }
    : null,
});

export const StartInfo = (data?: any): Types.IApi.StartInfo.Response => ({
  test: {
    id: get(data, 'test.id') || get(data, 'test._id') || '',
    title: get(data, 'test.title') || '',
    description: get(data, 'test.description') || '',
    totalQuestions: get(data, 'test.totalQuestions') || 0,
    timeLimit: get(data, 'test.timeLimit') ?? null,
    accessType: get(data, 'test.accessType') || undefined,
    priceSom: Number(get(data, 'test.priceSom')) || 0,
    purchased: !!get(data, 'test.purchased'),
    category: get(data, 'test.category') || undefined,
    sectionsCount: Number(get(data, 'test.sectionsCount')) || 0,
  },
  startPage: StartPageConfig(get(data, 'startPage')),
  attemptsUsed: get(data, 'attemptsUsed') || 0,
  attemptsAllowed: get(data, 'attemptsAllowed') ?? null,
  resumable: !!get(data, 'resumable'),
});

export const StartResponse = (data?: any): { attempt: Types.IEntity.Attempt; test: Types.TestForAttempt } => ({
  attempt: {
    id: get(data, 'attempt.id') || get(data, 'attempt._id') || '',
    testId: get(data, 'attempt.testId') || '',
    candidateId: get(data, 'attempt.candidateId') || '',
    status: get(data, 'attempt.status') || 'IN_PROGRESS',
    startedAt: get(data, 'attempt.startedAt') ? new Date(get(data, 'attempt.startedAt')) : new Date(),
    tabSwitches: get(data, 'attempt.tabSwitches') || 0,
    answers: (get(data, 'attempt.answers') || []).map((a: any) => AttemptAnswer(a)),
    events: (get(data, 'attempt.events') || []).map((e: any) => AttemptEvent(e)),
    test: TestForAttempt(get(data, 'test')),
    candidate: null,
  },
  test: TestForAttempt(get(data, 'test')),
});
